
import React, { useState, useEffect } from 'react';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  getCurrentMonthLeadImports, 
  getSubscriptionPlanByTier 
} from '@/services/subscriptions';
import { useAuth } from '@/context/AuthContext';

interface LeadQuotaIndicatorProps {
  refreshTrigger?: number;
}

const LeadQuotaIndicator = ({ refreshTrigger = 0 }: LeadQuotaIndicatorProps) => {
  const { subscription } = useAuth();
  const [leadImports, setLeadImports] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchImports = async () => {
      try {
        const imports = await getCurrentMonthLeadImports();
        setLeadImports(imports);
      } catch (error) {
        console.error('Error fetching lead imports:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchImports();
  }, [refreshTrigger]); // Re-fetch when refreshTrigger changes
  
  if (isLoading || !subscription) return null;
  
  const currentPlan = getSubscriptionPlanByTier(subscription.tier as any);
  const leadQuotaPercentage = Math.min(100, Math.round((leadImports / currentPlan.leadLimit) * 100));
  const isQuotaExceeded = leadImports >= currentPlan.leadLimit;
  
  return (
    <Card className={isQuotaExceeded ? 'border-yellow-500/50 bg-yellow-950/30 dark:border-yellow-500/40 dark:bg-yellow-950/20' : 'border-primary/20 bg-primary/5'}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            {isQuotaExceeded && <AlertCircle className="h-4 w-4 text-yellow-500 mr-2" />}
            <span className="font-medium text-sm">
              Lead Imports: {leadImports}/{currentPlan.leadLimit}
            </span>
          </div>
          <Link to="/subscription" className="text-xs text-primary hover:underline">
            {isQuotaExceeded ? 'Upgrade' : 'View Plan'}
          </Link>
        </div>
        <Progress value={leadQuotaPercentage} className="h-2" />
        {isQuotaExceeded && (
          <p className="text-xs text-yellow-500/80 mt-2">
            You've reached your monthly quota. Upgrade your plan to import more leads.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default LeadQuotaIndicator;
